import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	DeleteOutlined,
	DownloadOutlined,
	EditOutlined,
	MinusOutlined,
	PlusOutlined,
	ShareAltOutlined,
} from '@ant-design/icons';
import { translate } from '@smovilgps/optimus-intl';
import { Button, Col, Dropdown, Input, Popconfirm, Row, Space, Table } from 'antd-v6';
import _ from 'lodash';
import PropTypes from 'prop-types';
import 'rc-table/assets/index.css';

const CommonTable = ({
	columns,
	onSelectedChange,
	shareButton,
	getDevicesForShare,
	dataSource,
	loading,
	Add,
	devicesSelected,
	onSearch,
	getCheckboxProps,
	recordCount = 0,
	pageSize,
	onRow,
	eventEmmiter,
	exportAction,
	customEmptyMessage,
	clearSelection,
	unSelect,
	posActions,
	onDelete = () => {},
	size = 'small',
	scroll = {},
	hasPagination = true,
	advanceFilters = null,
	hideDelete = false,
	editable = false,
	caeNotificationButton = false,
	pointTable = false,
	exportButton = false,
	preserveSelectedRowKeys = true,
	hideSearchBar = false,
	disableDelete = false,
	showRowSelection = false,
}) => {
	const intl = useIntl();

	const selectedItemsRef = useRef([]);
	const [selectedItems, setSelectedItems] = useState([]);
	const [selectedDevices, setSelectedDevices] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);
	const [pagination, setPagination] = useState({
		defaultPageSize: pageSize ? pageSize : 15,
		pageSize: pageSize ? pageSize : 15,
		total: recordCount,
		current: 1,
	});
	const [search, setSearch] = useState('');
	const [order, setOrder] = useState('ascend');
	const [orderBy, setOrderBy] = useState('');

	useEffect(() => {
		if (eventEmmiter) {
			eventEmmiter.addListener('point_select', selectedPoi);
			eventEmmiter.addListener('managePoints', selectedPoi);
			eventEmmiter.addListener('endManagePoints', cleanSelection);
			eventEmmiter.addListener('last_select', checkIfSelectionEmpty);
			eventEmmiter.addListener('clean_selection', cleanSelection);
		}

		return () => {
			if (eventEmmiter) {
				eventEmmiter.removeListener('point_select', selectedPoi);
				eventEmmiter.removeListener('managePoints', selectedPoi);
				eventEmmiter.removeListener('endManagePoints', cleanSelection);
				eventEmmiter.removeListener('last_select', checkIfSelectionEmpty);
				eventEmmiter.removeListener('clean_selection', cleanSelection);
			}
		};
	}, [eventEmmiter]);

	const checkIfSelectionEmpty = () => {
		if (selectedItemsRef.current.length === 0) {
			eventEmmiter.emitEvent('endManagePoints', []);
		}
	};

	const selectedPoi = (pointId = 0, removePoi = false) => {
		if (!pointId) return;
		const items = removePoi
			? selectedItemsRef.current.filter((point) => point !== pointId)
			: _.uniq([...selectedItemsRef.current, pointId]);
		if (items.length === 0) {
			eventEmmiter.emitEvent('endManagePoints', []);
		}

		setSelectedItems(items);
		selectedItemsRef.current = items;
	};

	const cleanSelection = () => {
		setSelectedItems([]);
		setSelectedRows([]);
		selectedItemsRef.current = [];
		if (onSelectedChange) {
			onSelectedChange([], []);
		}
	};

	const onSelectAll = (added, current, before) => {
		if (!added && !current?.length) {
			cleanSelection();
			if (clearSelection) clearSelection();
		}

		if (eventEmmiter) {
			before.forEach((poi) => onSelect(poi, added));
		}
	};

	const onSelect = (row, isNew) => {
		if (!isNew && unSelect) {
			unSelect(row.id);
		}

		if (eventEmmiter) {
			eventEmmiter.emitEvent('tag_select_from_table', [row.id, !isNew]);
			eventEmmiter.emitEvent('point_select', [row.id, !isNew]);
		}
	};

	const remove = () => {
		const items = onDelete(selectedItems, selectedRows);
		setSelectedItems(items);
		selectedItemsRef.current = items;
	};

	const UpdateCaeNotification = (notifyCaeMx) => {
		UpdateCaeNotification(selectedItems, notifyCaeMx);
		eventEmmiter.emitEvent('clean_selection', []);
	};

	const onTableSearch = (value) => {
		value = value.trim();
		onSearch(value, 1, pagination.pageSize, order, orderBy);
		setSearch(value);
		setPagination({ ...pagination, current: 1 });
	};

	const handleSelectIems = (selected, objArray) => {
		let devices = [];
		if (caeNotificationButton) {
			devices = _.uniqBy([...selectedDevices, ...objArray], 'id').filter((device) =>
				selected.includes(device.id)
			);
		}
		setSelectedItems(selected);
		selectedItemsRef.current = selected;
		setSelectedDevices(devices);
		setSelectedRows(objArray);
		if (onSelectedChange) {
			onSelectedChange(selected, objArray);
		}
	};

	const onTableChange = (paging, filters, sorter) => {
		const order = sorter?.order ?? 'ascend';
		const orderBy = sorter?.order ? sorter.field : '';

		if (pagination.current !== paging.current) {
			setPagination({ ...pagination, ...paging });
		}
		setOrder(order);
		setOrderBy(orderBy);
		if (pointTable) {
			onSearch(search, pointTable);
		} else {
			onSearch(search, paging.current, paging.pageSize, order, orderBy);
		}
	};

	const getIcon = (icon) => {
		switch (icon) {
			case 'edit':
				return <EditOutlined />;
			case 'share-alt':
				return <ShareAltOutlined />;
			case 'download':
				return <DownloadOutlined />;
			case 'plus':
				return <PlusOutlined />;
			case 'minus':
				return <MinusOutlined />;
			default:
				return;
		}
	};

	const buttons = [
		{
			descriptionKey: 'editDevices',
			icon: 'edit',
			onClick: useCallback(() => devicesSelected(selectedItems), [selectedItems]),
			condition: editable,
			className: 'editable-add-btn',
		},
		{
			descriptionKey: 'shareDevicesAmount',
			icon: 'share-alt',
			onClick: useCallback(() => getDevicesForShare(selectedItems), [selectedItems]),
			condition: shareButton,
			className: 'editable-add-btn',
		},
		{
			descriptionKey: 'exportDevices',
			icon: 'download',
			onClick: useCallback(() => exportAction(selectedItems), [selectedItems]),
			condition: exportButton,
			className: 'editable-add-btn',
		},
		{
			descriptionKey: 'cae',
			icon: 'plus',
			onClick: UpdateCaeNotification,
			condition: caeNotificationButton,
			className: 'editable-add-btn',
			add: true,
			parameter: true,
		},
		{
			descriptionKey: 'cae',
			icon: 'minus',
			onClick: UpdateCaeNotification,
			condition: caeNotificationButton,
			className: 'editable-add-btn',
			remove: true,
			parameter: false,
		},
	];

	const renderActionMenu = useCallback(() => {
		const addNotifyCaeMx = selectedDevices.filter((device) => !device.notifyCaeMx);
		const removeNotifyCaeMx = selectedDevices.filter((device) => device.notifyCaeMx);
		const filteredActions = buttons.filter(
			(option) =>
				!(
					!selectedItems.length ||
					!option.condition ||
					(option.add && !addNotifyCaeMx.length) ||
					(option.remove && !removeNotifyCaeMx.length)
				)
		);

		if (!filteredActions.length) {
			return;
		}
		return filteredActions.map((option, key) => {
			return {
				key,
				onClick: () => {
					option.onClick(option.parameter);
					setSelectedDevices([]);
				},
				label: (
					<span data-testid={`menu-${option.descriptionKey}`}>
						{getIcon(option.icon)}
						<FormattedMessage
							{...translate(option.descriptionKey)}
							values={{ amountDevices: selectedItems.length }}
						/>
					</span>
				),
			};
		});
	}, [selectedItems]);

	const rowSelection = {
		onChange: handleSelectIems,
		type: 'checkbox',
		selectedRowKeys: selectedItems,
		getCheckboxProps: getCheckboxProps,
		onSelect: onSelect,
		onSelectAll: onSelectAll,
		preserveSelectedRowKeys: preserveSelectedRowKeys,
	};

	const items = renderActionMenu();
	const deleteActions = showRowSelection ? null : items ? (
		<Dropdown.Button
			menu={{ items }}
			style={{ width: '0px', display: 'inline' }}
			buttonsRender={([_, rightButton]) => [
				<Popconfirm
					disabled={disableDelete}
					title={intl.formatMessage(translate('deleteConfirm'))}
					onConfirm={remove}
					okButtonProps={{ style: { backgroundColor: 'var(--color-primary)', color: 'var(--bg-color)' } }}
					okText={
						<span data-testid="yes-btn">
							<FormattedMessage {...translate('yes')} />
						</span>
					}
					cancelText={intl.formatMessage(translate('no'))}
				>
					<Button data-testid="delete-btn" disabled={disableDelete}>
						<DeleteOutlined />
						<FormattedMessage
							{...translate('deleteAmount')}
							values={{
								amountDevices: selectedItems.length,
							}}
						/>
					</Button>
				</Popconfirm>,
				React.cloneElement(rightButton, {
					'data-testid': 'actions-btn',
				}),
			]}
		/>
	) : (
		<Popconfirm
			title={intl.formatMessage(translate('deleteConfirm'))}
			onConfirm={remove}
			okButtonProps={{ style: { backgroundColor: 'var(--color-primary)', color: 'var(--bg-color)' } }}
			okText={
				<span data-testid="yes-btn">
					<FormattedMessage {...translate('yes')} />
				</span>
			}
			cancelText={intl.formatMessage(translate('no'))}
		>
			<Button data-testid="delete-btn" style={{ marginLeft: '10px' }}>
				<DeleteOutlined />
				<FormattedMessage
					{...translate('deleteAmount')}
					values={{
						amountDevices: selectedItems.length,
					}}
				/>
			</Button>
		</Popconfirm>
	);

	const searchBar = !hideSearchBar && (
		<Col xs={{ span: 24 }} sm={{ span: 24 }} xl={{ span: 12 }}>
			<Space size={8} align="end" className="control" style={{ display: 'flex', justifyContent: 'flex-end' }}>
				{advanceFilters !== null && advanceFilters(selectedItems)}
				<Input.Search
					data-testid="search-bar"
					placeholder={intl.formatMessage(translate('searchPlaceholder'))}
					onSearch={(value) => onTableSearch(value)}
					className="searchBar"
					allowClear
				/>
				{posActions && posActions}
			</Space>
		</Col>
	);

	const tableTopComponents = (Add || selectedItems.length > 0 || !hideSearchBar) && (
		<Row className="control" type="flex">
			<Col className="action control" xs={{ span: 24 }} sm={{ span: 24 }} xl={{ span: 12 }}>
				<Space direction="horizontal" wrap>
					{Add}
					{selectedItems.length > 0 && deleteActions}
				</Space>
			</Col>
			{searchBar}
		</Row>
	);

	const paginationConfiguration = hasPagination
		? {
				...pagination,
				showSizeChanger: false,
				total: recordCount,
		  }
		: false;

	const locale = customEmptyMessage ? { emptyText: customEmptyMessage } : {};

	return (
		<div className="table">
			{tableTopComponents}
			<Row>
				<Col span={24}>
					<Table
						className={dataSource.length < 1 ? 'commonTable' : ''}
						size={size}
						rowKey={(record) => record.id}
						columns={columns}
						dataSource={dataSource}
						pagination={paginationConfiguration}
						rowSelection={hideDelete && !showRowSelection ? null : rowSelection}
						loading={loading}
						scroll={scroll}
						bordered
						onChange={onTableChange}
						onRow={onRow}
						locale={locale}
					/>
				</Col>
			</Row>
		</div>
	);
};

CommonTable.propTypes = {
	columns: PropTypes.array.isRequired,
	onSelectedChange: PropTypes.func,
	shareButton: PropTypes.bool,
	pointTable: PropTypes.bool,
	caeNotificationButton: PropTypes.bool,
	getDevicesForShare: PropTypes.func,
	dataSource: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired,
	Add: PropTypes.object,
	onDelete: PropTypes.func,
	devicesSelected: PropTypes.func,
	hideDelete: PropTypes.bool,
	onSearch: PropTypes.func.isRequired,
	getCheckboxProps: PropTypes.func.isRequired,
	recordCount: PropTypes.number,
	size: PropTypes.oneOf(['default', 'middle', 'small']),
	pageSize: PropTypes.number,
	scroll: PropTypes.object,
	onRow: PropTypes.func,
	editable: PropTypes.bool,
	eventEmmiter: PropTypes.object,
	advanceFilters: PropTypes.func,
	UpdateCaeNotification: PropTypes.func,
	hasPagination: PropTypes.bool,
	exportAction: PropTypes.func,
	exportButton: PropTypes.bool,
	preserveSelectedRowKeys: PropTypes.bool,
	hideSearchBar: PropTypes.bool,
	customEmptyMessage: PropTypes.object,
	clearSelection: PropTypes.func,
	unSelect: PropTypes.func,
	posActions: PropTypes.object,
	disableDelete: PropTypes.bool,
	showRowSelection: PropTypes.bool,
};

export default CommonTable;
